import * as jsonpatch from 'fast-json-patch';
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { BoardId } from '@shared/models/boardId';
import { BoardEvent } from '@shared/models/boardEvent';
import { ResourcesSet } from '@shared/models/events/resourcesSet';
import { ResourcesName } from '@shared/models/resourcesName';
import { ResourcesAdded } from '@shared/models/events/resourcesAdded';
import { ResourcesUpdated } from '@shared/models/events/resourcesUpdated';
import { ResourcesRemoved } from '@shared/models/events/resourcesRemoved';
import { ResourcesType } from '@shared/models/resourcesType';

export interface ResourcesState<TResource> {
  resources?: TResource[];
  errorStartTime?: number;
}

export interface ResourcesStates<TResource> {
  [boardId: BoardId]: ResourcesState<TResource>;
}

function getOrCreateResourcesState<TResource>(
  boardStates: ResourcesStates<TResource>,
  boardId: BoardId,
): ResourcesState<TResource> {
  let boardState = boardStates[boardId];
  if (!boardState) {
    boardState = boardStates[boardId] = {};
  }
  return boardState;
}

export function createResourcesSlice<TResource extends ResourcesType>(
  resourcesName: ResourcesName,
) {
  const initialState: ResourcesStates<TResource> = {};

  return createSlice({
    name: `${resourcesName}s`,
    initialState,
    reducers: {
      resourcesSet(state, action: PayloadAction<ResourcesSet<TResource>>) {
        const event = action.payload;
        const resourcesState = getOrCreateResourcesState(state, event.board);
        resourcesState.resources = event.set as Draft<TResource>[];
        resourcesState.errorStartTime = event.errorStart;
      },
      resourcesAdded(state, action: PayloadAction<ResourcesAdded<TResource>>) {
        const event = action.payload;
        const resourcesState = getOrCreateResourcesState(state, event.board);
        resourcesState.resources = [
          ...(resourcesState.resources ?? []),
          ...event.added,
        ] as Draft<TResource>[];
      },
      resourcesUpdated(state, action: PayloadAction<ResourcesUpdated>) {
        const event = action.payload;
        const resourcesState = getOrCreateResourcesState(state, event.board);
        if (!resourcesState.resources) {
          return;
        }
        for (const update of event.updates) {
          const [id, patch] = update;
          const resource = resourcesState.resources.find(
            (resourceCandidate) => resourceCandidate.id === id,
          );
          if (resource !== undefined) {
            jsonpatch.applyPatch(resource, patch);
          }
        }
      },
      resourcesRemoved(state, action: PayloadAction<ResourcesRemoved>) {
        const event = action.payload;
        const resourcesState = getOrCreateResourcesState(state, event.board);
        if (!resourcesState.resources) {
          return;
        }
        resourcesState.resources = resourcesState.resources.filter(
          (resource) => !event.ids.includes(resource.id),
        );
      },
      resourcesError(state, action: PayloadAction<BoardEvent>) {
        const event = action.payload;
        const resourcesState = getOrCreateResourcesState(state, event.board);
        resourcesState.errorStartTime = Date.now();
      },
      resourcesErrorResolved(state, action: PayloadAction<BoardEvent>) {
        const event = action.payload;
        const resourcesState = getOrCreateResourcesState(state, event.board);
        resourcesState.errorStartTime = undefined;
      },
    },
  });
}
